import { Check, Clock, MessageSquareText } from 'lucide-react';
import { ExpandableComment } from '@/components/common/ExpandableComment';
import { StatusBadge } from '@/components/common/StatusBadge';
import { Skeleton } from '@/components/ui/skeleton';
import { EMPTY_VALUE } from '@/constants/app.constants';
import { cn } from '@/lib/utils';
import { ACCENT_MARK_CLASS } from '@/styles/accentMark';
import { APPROVAL_STATUS_TONE } from '@/styles/statusColors';
import { formatDisplayDate } from '@/utils/date';
import type { Batch } from '../types/publish.types';

const TRAIL_STEP_NAMES = {
  published: { title: 'Published', actorLabel: 'Published by' },
  confirmed: { title: 'Confirmed', actorLabel: 'Confirmed by' },
  disbursed: { title: 'Disbursed', actorLabel: 'Disbursed by' },
} as const;

type TrailStepKey = keyof typeof TRAIL_STEP_NAMES;

const TRAIL_STEP_KEYS: readonly TrailStepKey[] = ['published', 'confirmed', 'disbursed'];

type TrailStepProgress = 'done' | 'current' | 'waiting';

const ARROW_PROGRESS_CLASS: Record<TrailStepProgress, string> = {
  done: ACCENT_MARK_CLASS,
  current: 'bg-primary/15 text-primary-strong',
  waiting: 'bg-muted text-muted-foreground',
};

const ARROW_SHAPE_CLASS = {
  first:
    'sm:-mr-2.5 sm:rounded-r-none sm:[clip-path:polygon(0_0,calc(100%_-_14px)_0,100%_50%,calc(100%_-_14px)_100%,0_100%)]',
  middle:
    'sm:-mr-2.5 sm:rounded-none sm:pl-6 sm:[clip-path:polygon(0_0,calc(100%_-_14px)_0,100%_50%,calc(100%_-_14px)_100%,0_100%,14px_50%)]',
  last: 'sm:rounded-l-none sm:pl-6 sm:[clip-path:polygon(0_0,100%_0,100%_100%,0_100%,14px_50%)]',
} as const;

type ArrowShape = keyof typeof ARROW_SHAPE_CLASS;

const DETAIL_INDENT_CLASS: Record<ArrowShape, string> = {
  first: 'px-3',
  middle: 'px-3 sm:pl-6',
  last: 'px-3 sm:pl-6',
};

interface TrailStep {
  who: string | null;
  when: string | null;
  note: string | null;
}

function getTrailStep(batch: Batch, key: TrailStepKey): TrailStep {
  if (key === 'published') {
    return { who: batch.publishedBy, when: batch.publishDate, note: batch.publishComment };
  }
  if (key === 'confirmed') {
    return { who: batch.confirmedBy, when: batch.confirmDate, note: batch.salesComment };
  }
  return { who: batch.disbursedBy, when: batch.disburseDate, note: batch.accountsComment };
}

const isStepDone = (step: TrailStep | undefined): boolean =>
  step !== undefined && (step.who !== null || step.when !== null);

function getStepProgress(
  steps: readonly (TrailStep | undefined)[],
  index: number,
): TrailStepProgress {
  if (isStepDone(steps[index])) return 'done';
  const firstPendingIndex = steps.findIndex((step) => step !== undefined && !isStepDone(step));
  return index === firstPendingIndex ? 'current' : 'waiting';
}

function getArrowShape(index: number): ArrowShape {
  if (index === 0) return 'first';
  if (index === TRAIL_STEP_KEYS.length - 1) return 'last';
  return 'middle';
}

interface TrailStepArrowProps {
  title: string;
  progress: TrailStepProgress;
  shape: ArrowShape;
}

function TrailStepArrow({ title, progress, shape }: TrailStepArrowProps) {
  const Icon = progress === 'done' ? Check : Clock;
  return (
    <div
      className={cn(
        'flex h-9 items-center gap-2 rounded-lg px-3 text-sm font-semibold',
        ARROW_PROGRESS_CLASS[progress],
        ARROW_SHAPE_CLASS[shape],
      )}
    >
      <Icon className="size-3.5" aria-hidden />
      {title}
    </div>
  );
}

interface TrailStepCommentProps {
  comment: string;
}

function TrailStepComment({ comment }: TrailStepCommentProps) {
  return (
    <div className="mt-2 rounded-lg bg-primary/5 px-2.5 py-1.5">
      <p className="flex items-center gap-1 text-xs text-muted-foreground">
        <MessageSquareText className="size-3" aria-hidden />
        Comment
      </p>
      <ExpandableComment comment={comment} className="text-xs-plus" />
    </div>
  );
}

interface TrailStepDetailProps {
  step: TrailStep | undefined;
}

function TrailStepDetail({ step }: TrailStepDetailProps) {
  if (step === undefined) {
    return (
      <>
        <Skeleton className="mt-1.5 h-4 w-32" />
        <Skeleton className="mt-1.5 h-3 w-20" />
      </>
    );
  }
  if (!isStepDone(step)) {
    return <p className="mt-1 text-sm text-muted-foreground">Not yet</p>;
  }
  return (
    <>
      <p className="mt-1 text-sm font-semibold">{step.who ?? EMPTY_VALUE}</p>
      <p className="text-xs text-muted-foreground">{formatDisplayDate(step.when)}</p>
      {step.note === null ? null : <TrailStepComment comment={step.note} />}
    </>
  );
}

interface TrailStepItemProps {
  stepKey: TrailStepKey;
  step: TrailStep | undefined;
  progress: TrailStepProgress;
  shape: ArrowShape;
}

function TrailStepItem({ stepKey, step, progress, shape }: TrailStepItemProps) {
  const { title, actorLabel } = TRAIL_STEP_NAMES[stepKey];
  const isDone = progress === 'done';
  return (
    <li className="min-w-0">
      <TrailStepArrow title={title} progress={progress} shape={shape} />
      <div className={cn('pt-3', DETAIL_INDENT_CLASS[shape])}>
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs text-muted-foreground">{actorLabel}</span>
          {step === undefined ? null : (
            <StatusBadge
              tone={isDone ? APPROVAL_STATUS_TONE.approved : APPROVAL_STATUS_TONE.pending}
            >
              {isDone ? 'Done' : 'Pending'}
            </StatusBadge>
          )}
        </div>
        <TrailStepDetail step={step} />
      </div>
    </li>
  );
}

interface BatchTrailProps {
  batch: Batch | undefined;
}

export function BatchTrail({ batch }: BatchTrailProps) {
  const steps = TRAIL_STEP_KEYS.map((key) =>
    batch === undefined ? undefined : getTrailStep(batch, key),
  );
  return (
    <ol className="grid grid-cols-1 gap-y-4 rounded-xl bg-card p-3 shadow-panel ring-1 ring-border sm:grid-cols-3 sm:gap-y-0">
      {TRAIL_STEP_KEYS.map((key, index) => (
        <TrailStepItem
          key={key}
          stepKey={key}
          step={steps[index]}
          progress={getStepProgress(steps, index)}
          shape={getArrowShape(index)}
        />
      ))}
    </ol>
  );
}
